import { useState, useEffect } from 'react';
import api from '../api';
import {
  Activity,
  RefreshCw,
  Filter,
  ChevronLeft,
  ChevronRight,
  User,
  Settings,
  Gamepad2,
  Bell,
  Shield,
  Calendar,
} from 'lucide-react';

const ACTION_ICONS = {
  user_updated: User,
  user_banned: User,
  user_deleted: User,
  password_reset: Shield,
  role_changed: Shield,
  game_updated: Gamepad2,
  game_override_deleted: Gamepad2,
  games_reordered: Gamepad2,
  games_synced: Gamepad2,
  setting_updated: Settings,
  setting_created: Settings,
  setting_deleted: Settings,
  notification_sent: Bell,
};

const ACTION_COLORS = {
  user_banned: 'text-red-400',
  user_deleted: 'text-red-400',
  password_reset: 'text-amber-400',
  notification_sent: 'text-blue-400',
  default: 'text-gray-400',
};

export default function ActivityLog() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });
  const [filters, setFilters] = useState({ action: '', entityType: '', startDate: '', endDate: '' });
  const [showFilters, setShowFilters] = useState(false);

  const fetchLogs = async (page = 1) => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ page, limit: 30 });
      if (filters.action) params.append('action', filters.action);
      if (filters.entityType) params.append('entityType', filters.entityType);
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);
      
      const response = await api.get(`/admin/activity?${params}`);
      setLogs(response.data.logs || []);
      setPagination(response.data.pagination || { page: 1, totalPages: 1, total: 0 });
    } catch (err) {
      console.error('Failed to fetch logs:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchLogs(); }, []);

  const applyFilters = () => {
    fetchLogs(1);
    setShowFilters(false);
  };

  const clearFilters = () => {
    setFilters({ action: '', entityType: '', startDate: '', endDate: '' });
    fetchLogs(1);
    setShowFilters(false);
  };

  const formatAction = (action) => action.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Activity className="text-amber-500" size={28} />
          <h1 className="text-2xl font-bold text-white">Activity Log</h1>
          <span className="text-gray-500 text-sm">({pagination.total} entries)</span>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg ${
              showFilters ? 'bg-amber-500 text-black' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            <Filter size={18} /> Filters
          </button>
          <button onClick={() => fetchLogs(pagination.page)} className="p-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600">
            <RefreshCw size={18} />
          </button>
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className="bg-gray-800 rounded-xl p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm text-gray-400 mb-1">Action</label>
              <select
                value={filters.action}
                onChange={(e) => setFilters({ ...filters, action: e.target.value })}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
              >
                <option value="">All Actions</option>
                <option value="user_updated">User Updated</option>
                <option value="user_banned">User Banned</option>
                <option value="password_reset">Password Reset</option>
                <option value="game_updated">Game Updated</option>
                <option value="notification_sent">Notification Sent</option>
                <option value="setting_updated">Setting Updated</option>
              </select>
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Entity Type</label>
              <select
                value={filters.entityType}
                onChange={(e) => setFilters({ ...filters, entityType: e.target.value })}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
              >
                <option value="">All Types</option>
                <option value="user">User</option>
                <option value="game">Game</option>
                <option value="setting">Setting</option>
                <option value="notification">Notification</option>
              </select>
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Start Date</label>
              <input
                type="date"
                value={filters.startDate}
                onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">End Date</label>
              <input
                type="date"
                value={filters.endDate}
                onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
              />
            </div>
          </div>
          <div className="flex justify-end gap-3 mt-4">
            <button onClick={clearFilters} className="px-4 py-2 text-gray-400 hover:text-white">Clear</button>
            <button onClick={applyFilters} className="px-4 py-2 bg-amber-500 text-black rounded-lg hover:bg-amber-400">Apply</button>
          </div>
        </div>
      )}

      {/* Activity Table */}
      <div className="bg-gray-800 rounded-xl overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-amber-500"></div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-700/50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Action</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Admin</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Entity</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Details</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Time</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {logs.length > 0 ? (
                  logs.map((log) => {
                    const Icon = ACTION_ICONS[log.action] || Activity;
                    const colorClass = ACTION_COLORS[log.action] || ACTION_COLORS.default;
                    return (
                      <tr key={log.id} className="hover:bg-gray-700/30">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <Icon size={16} className={colorClass} />
                            <span className="text-white">{formatAction(log.action)}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-400">{log.adminEmail || '-'}</td>
                        <td className="px-4 py-3">
                          <span className="px-2 py-1 bg-gray-700 rounded text-xs text-gray-300">
                            {log.entityType || '-'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-400 max-w-xs truncate">
                          {log.details ? JSON.stringify(log.details).slice(0, 60) : '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {new Date(log.createdAt).toLocaleString()}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={5} className="px-4 py-12 text-center text-gray-500">No activity logs found</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {!loading && logs.length > 0 && (
          <div className="px-4 py-3 border-t border-gray-700 flex items-center justify-between">
            <span className="text-sm text-gray-400">
              Page {pagination.page} of {pagination.totalPages}
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => fetchLogs(pagination.page - 1)}
                disabled={pagination.page <= 1}
                className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft size={18} />
              </button>
              <button
                onClick={() => fetchLogs(pagination.page + 1)}
                disabled={pagination.page >= pagination.totalPages}
                className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRight size={18} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
